import React from 'react';
import LoadingSpinner from './LoadingSpinner';
import { useAudio } from '../../contexts/AudioContext';
import './AudioModal.css';

const AudioModal = () => {
    const {
        currentAudio,
        isPlaying,
        currentTime,
        duration,
        volume,
        isEmbedded,
        isLoading,
        showFullModal,
        audioRef,
        closeAudio,
        minimizeToMini,
        togglePlayback,
        setCurrentTime,
        setDuration,
        setVolume,
        setIsPlaying,
        setIsLoading,
        getEmbeddableUrl,
        formatTime
    } = useAudio();



    if (!showFullModal || !currentAudio) {
        return null;
    }

    
    const handleAudioTimeUpdate = (audioElement) => {
        setCurrentTime(audioElement.currentTime);
        setDuration(audioElement.duration || 0);
    };

    const handleAudioSeek = (audioElement, newTime) => {
        audioElement.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const handleAudioVolumeChange = (audioElement, newVolume) => {
        audioElement.volume = newVolume;
        setVolume(newVolume);
    };

        return (
        <>
            {showFullModal && (
                <div className="audio-modal-overlay" onClick={closeAudio}>
                    <div className="audio-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="audio-modal-header">
                            <h3>{currentAudio.topic}</h3>
                            <div className="audio-modal-header-controls">
                                {/* Only show minimize button for direct audio files (MP3, WAV, etc.) */}
                                {!isEmbedded && (
                                    <button 
                                        className="header-btn minimize" 
                                        onClick={minimizeToMini}
                                        title="Minimize player"
                                    >
                                        −
                                    </button>
                                )}
                                <button className="header-btn close" onClick={closeAudio}>
                                    ×
                                </button>
                            </div>
                        </div>
                    
                    <div className="audio-modal-content">
                        {isLoading && (
                            <div className="media-loading">
                                <LoadingSpinner />
                            </div>
                        )}
                        
                        {isEmbedded ? (
                            /* Embedded Platform Audio - show the actual player */
                            <div className="embedded-audio-player">
                                <iframe
                                    src={getEmbeddableUrl(currentAudio.webURLLink)}
                                    width="100%"
                                    height="352"
                                    frameBorder="0"
                                    allowtransparency="true"
                                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                    loading="lazy"
                                    title={currentAudio.topic}
                                    onLoad={() => {
                                        setIsLoading(false);
                                    }}
                                ></iframe>
                            </div>
                    ) : (
                        /* Standard Audio Element with Waveform */
                        <div className="audio-waveform-container">
                            <div className="audio-waveform">
                                <i className="fas fa-music audio-icon"></i>
                                <div className="waveform-bars">
                                    {[...Array(20)].map((_, i) => (
                                        <div 
                                            key={i} 
                                            className={`waveform-bar ${isPlaying ? 'animating' : ''}`}
                                            style={{
                                                animationDelay: `${i * 0.1}s`,
                                                height: `${Math.random() * 40 + 20}px`
                                            }}
                                        ></div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Audio Element is now persistent in AudioContext */}
                        </div>
                    )}

                    {/* Audio Controls - Only show for regular audio files, not embedded platforms */}
                    {!isEmbedded && (
                        <div className="audio-controls">
                            <div className="main-controls">
                                <button 
                                    className="control-btn skip-back"
                                    onClick={() => {
                                        if (audioRef.current) {
                                            handleAudioSeek(audioRef.current, Math.max(0, currentTime - 15));
                                        }
                                    }}
                                    title="Skip back 15s"
                                >
                                    <i className="fas fa-backward"></i>
                                    <span>15</span>
                                </button>
                                
                                <button 
                                    className={`control-btn play-pause ${isPlaying ? 'playing' : ''}`}
                                    onClick={togglePlayback}
                                >
                                    <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
                                </button>
                                
                               
                                
                                <button 
                                    className="control-btn skip-forward"
                                    onClick={() => {
                                        if (audioRef.current) {
                                            handleAudioSeek(audioRef.current, Math.min(duration, currentTime + 15));
                                        }
                                    }}
                                    title="Skip forward 15s"
                                >
                                    <i className="fas fa-forward"></i>
                                    <span>15</span>
                                </button>
                            </div>

                            {/* Progress Bar */}
                            <div className="progress-container">
                                <span className="time-display current-time">
                                    {formatTime(currentTime)}
                                </span>
                                <div className="progress-bar">
                                    <div 
                                        className="progress-fill"
                                        style={{ 
                                            width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' 
                                        }}
                                    ></div>
                                    <input
                                        type="range"
                                        className="progress-slider"
                                        min="0"
                                        max={duration || 0}
                                        value={currentTime}
                                        onChange={(e) => {
                                            if (audioRef.current) {
                                                handleAudioSeek(audioRef.current, parseFloat(e.target.value));
                                            }
                                        }}
                                    />
                                </div>
                                <span className="time-display total-time">
                                    {formatTime(duration)}
                                </span>
                            </div>

                            {/* Volume Control */}
                            <div className="volume-control">
                                <i className={`fas ${volume === 0 ? 'fa-volume-mute' : volume < 0.5 ? 'fa-volume-down' : 'fa-volume-up'}`}></i>
                                <input
                                    type="range"
                                    className="volume-slider"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={volume}
                                    onChange={(e) => {
                                        if (audioRef.current) {
                                            handleAudioVolumeChange(audioRef.current, parseFloat(e.target.value));
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* Audio Info Section */}
                    <div className="audio-modal-info">
                        <p>{currentAudio.shortDescription}</p>
                        <div className="audio-modal-meta">
                            {currentAudio.duration && (
                                <span><i className="fas fa-clock"></i> {currentAudio.duration}</span>
                            )}
                            <span><i className="fas fa-calendar"></i> {new Date(currentAudio.publishDate).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                            })}</span>
                            <span><i className="fas fa-building"></i> {currentAudio.entity}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
            )}
        </>
    );
};

export default AudioModal; 